import { useEffect, useRef, useState } from 'react';
import { X, Edit2, Check } from 'lucide-react';
import type { Note, BookMetadata } from './types';
import { HighlightColor } from './types';
import ReferenceActionBar from './ReferenceActionBar';
import { useTranslation } from '../../i18n/useTranslation';

interface HighlightNoteModalProps {
  note: Note;
  onClose: () => void;
  onUpdate?: (id: string, color: HighlightColor, noteText?: string) => void | Promise<void>;
  onDelete?: () => void;
  isOffline?: boolean; // NEW: Hide edit button in offline mode
  bookMetadata?: BookMetadata | null;
}

export default function HighlightNoteModal({
  note,
  onClose,
  onUpdate,
  onDelete,
  isOffline = false, // Default to false (online mode)
  bookMetadata,
}: HighlightNoteModalProps) {
  // Safety check - if note is undefined, don't render
  if (!note) {
    return null;
  }

  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editedColor, setEditedColor] = useState<HighlightColor>(note.color as HighlightColor);
  const [editedNoteText, setEditedNoteText] = useState(note.noteText || '');
  const [isSaving, setIsSaving] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  if (previousFocusRef.current === null && typeof document !== 'undefined') {
    previousFocusRef.current = document.activeElement as HTMLElement | null;
  }
  useEffect(() => {
    const dialog = dialogRef.current;
    dialog?.focus();
    return () => {
      requestAnimationFrame(() => {
        if (!dialog?.isConnected && previousFocusRef.current) {
          previousFocusRef.current.focus?.();
        }
      });
    };
  }, []);

  const colors: { color: HighlightColor; label: string }[] = [
    { color: HighlightColor.Yellow, label: t('App.Reader.Highlight.Color.Yellow') },
    { color: HighlightColor.Green, label: t('App.Reader.Highlight.Color.Green') },
    { color: HighlightColor.Blue, label: t('App.Reader.Highlight.Color.Blue') },
    { color: HighlightColor.Pink, label: t('App.Reader.Highlight.Color.Pink') },
    { color: HighlightColor.Purple, label: t('App.Reader.Highlight.Color.Purple') },
    { color: HighlightColor.Orange, label: t('App.Reader.Highlight.Color.Orange') },
    { color: HighlightColor.Red, label: t('App.Reader.Highlight.Color.Red') },
    { color: HighlightColor.Teal, label: t('App.Reader.Highlight.Color.Teal') },
    { color: HighlightColor.Lime, label: t('App.Reader.Highlight.Color.Lime') },
    { color: HighlightColor.Amber, label: t('App.Reader.Highlight.Color.Amber') },
  ];

  const handleSave = async () => {
    if (!onUpdate) return;

    setIsSaving(true);
    try {
      await onUpdate(note.id, editedColor, editedNoteText || undefined);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update highlight:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      role="presentation"
      className="fixed inset-0 bg-black bg-opacity-50 z-[1100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="highlight-note-title"
        tabIndex={-1}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        className="bg-white rounded-lg shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 id="highlight-note-title" className="text-lg font-semibold text-neutral-800">{t('App.Reader.Highlight.ModalTitle')}</h3>
          <div className="flex items-center gap-2">
            {!isEditing && onUpdate && !isOffline && ( // Hide edit button in offline mode
              <button
                onClick={() => setIsEditing(true)}
                className="p-1 hover:bg-neutral-100 rounded transition-colors"
                title={t('App.Reader.Highlight.EditAriaLabel')}
                aria-label={t('App.Reader.Highlight.EditAriaLabel')}
              >
                <Edit2 className="w-5 h-5 text-neutral-500" aria-hidden="true" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1 hover:bg-neutral-100 rounded transition-colors"
              aria-label={t('App.Reader.Highlight.CloseLabel')}
            >
              <X className="w-5 h-5 text-neutral-500" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Highlighted Text */}
          <div>
            <div className="text-xs font-medium text-neutral-500 mb-2">
              {t('App.Reader.Highlight.TextLabel')}
            </div>
            <div
              className="p-3 rounded"
              style={{ backgroundColor: isEditing ? editedColor : note.color, opacity: 0.9 }}
            >
              <p className="text-sm font-medium text-neutral-800">
                "{note.selectedText}"
              </p>
            </div>
          </div>

          {/* Color Picker (Edit Mode) */}
          {isEditing && (
            <div>
              <div className="text-xs font-medium text-neutral-500 mb-2">
                {t('App.Reader.Highlight.ChangeColorLabel')}
              </div>
              <div className="grid grid-cols-5 gap-2">
                {colors.map(({ color, label }) => (
                  <button
                    key={color}
                    onClick={() => setEditedColor(color)}
                    className={`w-10 h-10 rounded-md border-2 transition-all transform hover:scale-110 shadow-sm ${
                      editedColor === color
                        ? 'border-neutral-800 ring-2 ring-neutral-400'
                        : 'border-transparent hover:border-neutral-400'
                    }`}
                    style={{ backgroundColor: color }}
                    title={label}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Note */}
          <div>
            <div className="text-xs font-medium text-neutral-500 mb-2">
              {isEditing ? t('App.Reader.Highlight.EditNoteLabel') : t('App.Reader.Highlight.NoteLabel')}
            </div>
            {isEditing ? (
              <textarea
                value={editedNoteText}
                onChange={(e) => setEditedNoteText(e.target.value)}
                placeholder={t('App.Reader.Highlight.NotePlaceholder')}
                className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                rows={4}
              />
            ) : note.noteText ? (
              <div className="p-3 bg-neutral-50 rounded">
                <p className="text-sm text-neutral-700">{note.noteText}</p>
              </div>
            ) : (
              <p className="text-sm text-neutral-400 italic">{t('App.Reader.Highlight.NoNoteAdded')}</p>
            )}
          </div>

          {/* Location */}
          <div className="text-xs text-neutral-400">
            {note.locationLabel}
          </div>
        </div>

        {/* Copy / Cite / Export actions (view mode only) */}
        {!isEditing && (
          <ReferenceActionBar
            selectedText={note.selectedText}
            bookMetadata={bookMetadata}
            locationLabel={note.locationLabel}
          />
        )}

        {/* Footer */}
        <div className="px-6 py-4 bg-neutral-50 border-t border-neutral-200 flex justify-between sticky bottom-0">
          {isEditing ? (
            <>
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditedColor(note.color as HighlightColor);
                  setEditedNoteText(note.noteText || '');
                }}
                className="px-4 py-2 text-neutral-600 hover:bg-neutral-200 rounded-lg transition-colors text-sm font-medium"
                disabled={isSaving}
              >
                {t('App.Reader.Highlight.CancelButton')}
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('App.Reader.Highlight.Saving')}
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    {t('App.Reader.Highlight.SaveButton')}
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              {onDelete && !isOffline && ( // Hide delete button in offline mode
                <button
                  onClick={() => {
                    onDelete();
                    onClose();
                  }}
                  className="px-4 py-2 text-error-600 hover:bg-error-50 rounded-lg transition-colors text-sm font-medium"
                >
                  {t('App.Reader.Highlight.DeleteButton')}
                </button>
              )}
              <button
                onClick={onClose}
                className="ml-auto px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
              >
                {t('App.Reader.Highlight.CloseLabel')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
